// Copyright ©️ 2026 eVolve MEP, LLC

import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from 'react';

import { ActionIcon, Box, Button, Switch, Text, UnstyledButton } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { StepperInput } from 'components/Mantine/StepperInput';
import { isNotNil } from 'helpers/isNotNil';
import classes from 'modules/Shop/EvolveFabrication/BendCreator/Cart/BendCart.module.css';
import {
  type CartBend,
  type CartBendId,
  useBendCart,
} from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import {
  BEND_TYPE_STEP,
  type BendCreatorStepIndex,
  DIMENSIONS_STEP,
  MATERIAL_STEP,
} from 'modules/Shop/EvolveFabrication/BendCreator/Nav/bendCreatorSteps';
import {
  formatBendDetails,
  formatDimensionsSummary,
  formatMaterial,
} from 'modules/Shop/EvolveFabrication/BendCreator/Nav/stepValues';
import { useBendBuilder } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';
import { type CopyEditStatus, useCopyEditing } from 'modules/Shop/EvolveFabrication/BendCreator/State/useCopyEditing';
import { useBendTypes } from 'modules/Shop/EvolveFabrication/BendCreator/useBendTypes';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

/** Stops a click on a row's control from also selecting the row. */
const stopRowClick = (event: MouseEvent) => event.stopPropagation();

const editStatusTags: Record<CopyEditStatus, string> = {
  valid: 'Editing',
  validating: 'Validating',
  invalid: 'Invalid',
};

type NameJumpRowProps = {
  name: string;
  onRename: (name: string) => void;
};

/** The editable Name row of an open cart row. Commits on Enter or leaving the field; an empty name reverts. */
const NameJumpRow = ({ name, onRename }: NameJumpRowProps) => {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (isNotNil(draft) && draft.trim() !== '' && draft !== name) onRename(draft.trim());
    setDraft(null);
  };
  return (
    <Box component="label" className={`${classes.jumpRow} ${classes.nameRow}`}>
      <Text component="span" className={classes.jumpLabel}>
        Name
      </Text>
      <Box
        component="input"
        className={`${classes.jumpValue} ${classes.nameInput}`}
        value={draft ?? name}
        onChange={(event: { currentTarget: { value: string } }) => setDraft(event.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') setDraft(null);
        }}
        aria-label="Name"
      />
    </Box>
  );
};

type StepJumpRowProps = {
  label: string;
  value: string | null;
  active: boolean;
  onOpen: () => void;
};

const StepJumpRow = ({ label, value, active, onOpen }: StepJumpRowProps) => (
  <UnstyledButton className={classes.jumpRow} data-active={active || undefined} onClick={onOpen}>
    <Text component="span" className={classes.jumpLabel}>
      {label}
    </Text>
    <Text component="span" className={classes.jumpValue}>
      {value ?? '--'}
    </Text>
    <EvolveIcon icon="ArrowRight" size="xs" className={classes.chevron} />
  </UnstyledButton>
);

const jumpSteps: { label: string; step: BendCreatorStepIndex }[] = [
  { label: 'Material', step: MATERIAL_STEP },
  { label: 'Shape', step: BEND_TYPE_STEP },
  { label: 'Dimensions', step: DIMENSIONS_STEP },
];

type CartQuantityStepperProps = {
  cartBendId: CartBendId;
  quantity: number;
};

const CartQuantityStepper = ({ cartBendId, quantity }: CartQuantityStepperProps) => {
  const { changeListedBend } = useCopyEditing();
  return (
    <StepperInput
      mode="count"
      compact
      aria-label="Quantity"
      min={1}
      max={99}
      step={1}
      value={quantity}
      onChange={(newQuantity) => {
        if (isNotNil(newQuantity)) changeListedBend(cartBendId, { quantity: newQuantity });
      }}
    />
  );
};

type CartRowProps = {
  bend: CartBend;
  /** 1-based position in the list. */
  number: number;
};

const CartRow = ({ bend: { cartBendId, values }, number }: CartRowProps) => {
  const {
    previewedBendId,
    previewMarked,
    expandedBendId,
    justAddedBendId,
    previewBend,
    expandBend,
    collapseBend,
    duplicateBend,
    removeBend,
  } = useBendCart();
  const { editingBendId, status, startEditing, finishEditing, changeListedBend } = useCopyEditing();
  const { nav, currentStep } = useBendBuilder();
  const { bendTypes } = useBendTypes();
  const { conduitTypes, conduitSizes } = useConduits();
  const bendType = bendTypes?.find(({ name }) => name === values.bendType);
  const conduitLists = { conduitTypes, conduitSizes };

  const expanded = expandedBendId === cartBendId;
  const editing = editingBendId === cartBendId;
  const selected = previewedBendId === cartBendId && previewMarked;
  const locked = isNotNil(expandedBendId) && !expanded;
  const justAdded = justAddedBendId === cartBendId;

  // A new row scrolls itself into view once, when it's added.
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (justAdded) rowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [justAdded]);

  // Only one row can be open, and only the open row can be edited, so leave any other edit first.
  const leaveOtherEdit = () => {
    if (isNotNil(editingBendId) && !editing) finishEditing();
  };
  const toggleExpanded = () => {
    if (expanded) {
      if (editing) finishEditing();
      collapseBend();
    } else {
      leaveOtherEdit();
      expandBend(cartBendId);
    }
  };
  const duplicate = () => {
    if (isNotNil(editingBendId)) finishEditing();
    duplicateBend(cartBendId);
  };
  const remove = () => {
    if (editing) finishEditing();
    removeBend(cartBendId);
  };
  const done = () => {
    if (editing) finishEditing();
    collapseBend();
  };

  return (
    <Box
      ref={rowRef}
      className={classes.row}
      data-selected={selected || undefined}
      data-expanded={expanded || undefined}
      data-locked={locked || undefined}
      data-new={justAdded || undefined}
      onClick={() => previewBend(cartBendId)}
    >
      <Box className={classes.titleLine}>
        <Text component="span" inherit className={classes.number}>
          #{number}
        </Text>
        <Text component="span" inherit className={classes.name}>
          {values.partName}
        </Text>
        {editing && isNotNil(status) && (
          <Text component="span" className={classes.tag} data-invalid={status === 'invalid' || undefined}>
            {editStatusTags[status]}
          </Text>
        )}
        <ActionIcon
          className={classes.rowButton}
          variant="default"
          data-active={expanded || undefined}
          aria-label={expanded ? `Close ${values.partName}` : `Edit ${values.partName}`}
          aria-pressed={expanded}
          onClick={(event) => {
            stopRowClick(event);
            toggleExpanded();
          }}
        >
          <EvolveIcon icon="Edit" size="sm" />
        </ActionIcon>
        <ActionIcon
          className={classes.rowButton}
          variant="default"
          aria-label={`Remove ${values.partName} from the bend list`}
          onClick={(event) => {
            stopRowClick(event);
            remove();
          }}
        >
          <EvolveIcon icon="Delete" size="sm" />
        </ActionIcon>
      </Box>
      {expanded ? (
        <>
          <Box className={classes.jumpList} onClick={stopRowClick}>
            <NameJumpRow name={values.partName} onRename={(partName) => changeListedBend(cartBendId, { partName })} />
            {jumpSteps.map(({ label, step }) => (
              <StepJumpRow
                key={step}
                label={label}
                value={
                  step === MATERIAL_STEP
                    ? formatMaterial(values, conduitLists)
                    : step === BEND_TYPE_STEP
                      ? values.bendType
                      : step === DIMENSIONS_STEP
                        ? formatDimensionsSummary(values, bendType)
                        : values.benderProfile.name
                }
                active={editing && nav === 'step' && currentStep === step}
                onOpen={() => startEditing(cartBendId, step)}
              />
            ))}
          </Box>
          <Box className={classes.actionLine} onClick={stopRowClick}>
            <CartQuantityStepper cartBendId={cartBendId} quantity={values.quantity} />
            <Button
              className={classes.smallButton}
              variant="default"
              disabled={editing && status !== 'valid'}
              onClick={done}
              leftSection={<EvolveIcon icon="Check" size="sm" />}
            >
              Done
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Text className={classes.details}>{formatBendDetails(values, bendType, conduitLists)}</Text>
          <Box className={classes.actionLine} onClick={stopRowClick}>
            <Button
              className={classes.smallButton}
              variant="default"
              leftSection={<EvolveIcon icon="Copy" size="sm" />}
              onClick={duplicate}
            >
              Duplicate
            </Button>
            <CartQuantityStepper cartBendId={cartBendId} quantity={values.quantity} />
          </Box>
        </>
      )}
    </Box>
  );
};

type Props = {
  onAddToWorkOrder: () => void;
};

/**
 * The bend list at the right of the Bend Creator. Hidden while the list is empty; collapses to a pill with a count.
 */
export const BendCart = ({ onAddToWorkOrder }: Props) => {
  const { bends, cartOpen, setCartOpen } = useBendCart();
  const { status } = useCopyEditing();
  // Fast track is not wired up yet: the switch only holds its own state.
  const [fastTrack, setFastTrack] = useState(false);

  if (bends.length === 0) return null;

  if (!cartOpen) {
    return (
      <UnstyledButton className={classes.pill} onClick={() => setCartOpen(true)} aria-label="Open the bend list">
        Bend List
        <Box component="span" className={classes.badge}>
          {bends.length}
        </Box>
      </UnstyledButton>
    );
  }

  return (
    <Box className={classes.panel}>
      <Box className={classes.header}>
        <Text component="span" className={classes.headerTitle}>
          Bend List
        </Text>
        <ActionIcon
          className={classes.collapseButton}
          variant="subtle"
          color="gray"
          aria-label="Collapse the bend list"
          onClick={() => setCartOpen(false)}
        >
          <EvolveIcon icon="ArrowRight" size="sm" />
        </ActionIcon>
      </Box>
      <Box className={classes.body}>
        {bends.map((bend, index) => (
          <CartRow key={bend.cartBendId} bend={bend} number={index + 1} />
        ))}
      </Box>
      <Box className={classes.footer}>
        <Switch
          classNames={{
            body: classes.fastTrackBody,
            labelWrapper: classes.fastTrackLabelWrapper,
            label: classes.fastTrackLabel,
            description: classes.fastTrackDescription,
          }}
          labelPosition="left"
          label="Fast Track Work Order"
          description="Bypass work order approval and immediately release"
          checked={fastTrack}
          onChange={(event) => setFastTrack(event.currentTarget.checked)}
        />
        <Button
          className={classes.footerButton}
          // An edited bend must pass validation before the list can be sent.
          disabled={isNotNil(status) && status !== 'valid'}
          onClick={onAddToWorkOrder}
          leftSection={<EvolveIcon icon="Check" size="sm" />}
        >
          Add to work order
        </Button>
      </Box>
    </Box>
  );
};
